import APP_CONFIG from '../config';

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

function pickAgentAvatar(displayName) {
  const n = (displayName || '').toLowerCase();
  if (n.includes('hr') || n.includes('human')) return APP_CONFIG.DEFAULT_AVATARS.HR;
  if (n.includes('mkt') || n.includes('market')) return APP_CONFIG.DEFAULT_AVATARS.MARKETING;
  if (n.includes('dev') || n.includes('eng') || n.includes('tech')) return APP_CONFIG.DEFAULT_AVATARS.DEVELOPER;
  if (n.includes('support')) return APP_CONFIG.DEFAULT_AVATARS.SUPPORT;
  if (n.includes('sales')) return APP_CONFIG.DEFAULT_AVATARS.SALES;
  if (n.includes('design')) return APP_CONFIG.DEFAULT_AVATARS.DESIGN;
  if (n.includes('finance')) return APP_CONFIG.DEFAULT_AVATARS.FINANCE;
  if (n.includes('ops') || n.includes('operat')) return APP_CONFIG.DEFAULT_AVATARS.OPERATIONS;
  return APP_CONFIG.DEFAULT_AVATARS.GENERIC;
}

export function buildAgentFromServer(name, data = {}) {
  const kernel = data.kernel || {};
  const displayName = kernel.name || name;
  return {
    id: name,
    name: displayName,
    avatar: pickAgentAvatar(displayName),
    status: 'active',
    description: kernel.personality || kernel.role || 'AI agent',
    role: kernel.role || '',
    subChats: data.sub_chats || [],
  };
}

export function getApiBase() {
  return (APP_CONFIG.API_BASE_URL || '').replace(/\/$/, '');
}

// -------------------------------------------------------------------------- //
//  Auth helpers                                                                //
// -------------------------------------------------------------------------- //

export async function registerUser(username, password) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/register`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ username, password }),
  });
  return requireOk(res, 'Registration failed'); // { token, username }
}

export async function loginUser(username, password) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/login`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ username, password }),
  });
  return requireOk(res, 'Login failed'); // { token, username }
}

export async function verifyToken(token) {
  const base = getApiBase();
  try {
    const res = await fetch(`${base}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}`, ...jsonHeaders },
    });
    if (!res.ok) return null;
    return await res.json(); // { username, role, created_at }
  } catch {
    return null;
  }
}

export async function changePassword(oldPassword, newPassword, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/change-password`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
  return requireOk(res, 'Failed to change password');
}

export async function createUserAsAdmin({ username, password, role }, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/admin/create-user`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ username, password, role }),
  });
  return requireOk(res, 'Failed to create user');
}

export async function listUsersAsAdmin(token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/admin/users`, {
    headers: getAuthHeaders(token),
  });
  const data = await requireOk(res, 'Failed to load users');
  return Array.isArray(data.users) ? data.users : [];
}

export async function deleteUserAsAdmin(userId, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/auth/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
  return requireOk(res, 'Failed to remove user');
}

export async function exportAllData(token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/export/all`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) await requireOk(res, 'Export failed');
  return res.blob();
}

export async function exportExcelData(token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/export/excel`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) await requireOk(res, 'Excel export failed');
  return res.blob();
}

function getAuthHeaders(token) {
  return {
    ...jsonHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatApiError(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        const label = field ? `${String(field).replace(/_/g, ' ')}: ` : '';
        return `${label}${item?.msg || fallback}`;
      })
      .join(' ');
  }
  if (typeof detail === 'object') {
    return detail.message || detail.msg || fallback;
  }
  return fallback;
}

async function readJsonResponse(res) {
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

async function requireOk(res, fallback) {
  const data = await readJsonResponse(res);
  if (!res.ok) {
    const error = new Error(formatApiError(data.detail || data.message, fallback));
    error.status = res.status;
    throw error;
  }
  return data;
}

export async function fetchHealth() {
  const base = getApiBase();
  const res = await fetch(`${base}/health`, { method: 'GET' });
  if (!res.ok) throw new Error(`Health check failed: ${res.status}`);
  return res.json();
}

export async function fetchAgentNames(token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agents`, {
    headers: getAuthHeaders(token),
  });
  const data = await requireOk(res, 'Failed to list agents');
  return Array.isArray(data.agents) ? data.agents : [];
}

export async function hydrateAgents(sessionId, token) {
  const base = getApiBase();
  const url = new URL(`${base}/api/agents/hydrate`);
  url.searchParams.set('session_id', sessionId);
  const res = await fetch(url.toString(), {
    headers: getAuthHeaders(token),
  });
  return requireOk(res, 'Hydration failed'); // { agents: [{ id, name, kernel, session, scroll_position, sub_chats }] }
}

export async function fetchKernel(agentName, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(agentName)}/kernel`, {
    headers: getAuthHeaders(token),
  });
  return requireOk(res, `Failed to load kernel for ${agentName}`);
}

export async function saveKernel(agentName, kernel, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(agentName)}/kernel`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify(kernel),
  });
  return requireOk(res, `Failed to save kernel for ${agentName}`);
}

export async function fetchSession(agentName, sessionId, token, subChatId) {
  const base = getApiBase();
  const url = new URL(`${base}/api/session/${encodeURIComponent(agentName)}`);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  if (subChatId) url.searchParams.set('sub_chat_id', subChatId);
  const res = await fetch(url.toString(), {
    headers: {
      ...getAuthHeaders(token),
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
    },
  });
  return requireOk(res, `Failed to load session for ${agentName}`);
}

export async function fetchScrollPosition(agentName, sessionId, token, subChatId) {
  const base = getApiBase();
  const url = new URL(`${base}/api/session/${encodeURIComponent(agentName)}/scroll`);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  if (subChatId) url.searchParams.set('sub_chat_id', subChatId);
  try {
    const res = await fetch(url.toString(), {
      headers: {
        ...getAuthHeaders(token),
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
    });
    if (!res.ok) return 0;
    const data = await res.json();
    return data.scroll_position || 0;
  } catch {
    return 0;
  }
}

export async function saveScrollPosition(agentName, sessionId, position, token, subChatId) {
  const base = getApiBase();
  const url = new URL(`${base}/api/session/${encodeURIComponent(agentName)}/scroll`);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  if (subChatId) url.searchParams.set('sub_chat_id', subChatId);
  try {
    await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(token),
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify({ position: Math.round(position) }),
    });
  } catch { /* fire-and-forget */ }
}

export async function clearSessionHistory(agentName, sessionId, token, subChatId) {
  const base = getApiBase();
  const url = new URL(`${base}/api/session/${encodeURIComponent(agentName)}/history`);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  if (subChatId) url.searchParams.set('sub_chat_id', subChatId);
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(token),
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
    },
  });
  return requireOk(res, 'Failed to clear chat');
}

export async function createAgentOnServer({ name, role = '', personality = '' }, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agents`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ name, role, personality }),
  });
  return requireOk(res, 'Failed to create agent');
}

export async function duplicateAgentOnServer(name, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(name)}/duplicate`, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
  return requireOk(res, 'Failed to duplicate agent');
}

export async function renameAgentOnServer(name, newName, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(name)}/rename`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ name: newName }),
  });
  return requireOk(res, 'Failed to rename agent');
}

export async function createSubChatOnServer(agentName, name, token, sessionId) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(agentName)}/subchat`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ name, session_id: sessionId }),
  });
  return requireOk(res, 'Failed to create sub-chat');
}

export async function renameSubChatOnServer(subChatId, newName, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/subchat/${encodeURIComponent(subChatId)}`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ name: newName }),
  });
  return requireOk(res, 'Failed to rename sub-chat');
}

export async function deleteSubChatOnServer(subChatId, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/subchat/${encodeURIComponent(subChatId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
  return requireOk(res, 'Failed to delete sub-chat');
}

export async function deleteAgentOnServer(name, token) {
  const base = getApiBase();
  const res = await fetch(`${base}/api/agent/${encodeURIComponent(name)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
  return requireOk(res, 'Failed to delete agent');
}

/**
 * POST /api/message — streams SSE: data: {"chunk":"..."} and data: {"done":true}
 */
export async function postMessageStream(
  agent,
  message,
  { signal, onChunk, onDone, onError, sessionId, token, subChatId } = {}
) {
  const base = getApiBase();
  let res;
  try {
    res = await fetch(`${base}/api/message`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(token),
        Accept: 'text/event-stream',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify({
        agent,
        message,
        session_id: sessionId,
        sub_chat_id: subChatId
      }),
      signal,
    });
  } catch (e) {
    if (onError) onError(e);
    return;
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch {
      try {
        detail = await res.text();
      } catch {
        /* ignore */
      }
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    if (onError) onError(err);
    return;
  }

  if (!res.body || !res.body.getReader) {
    if (onError) onError(new Error('Streaming not supported in this browser'));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processLine = (line) => {
    const trimmed = line.trimEnd();
    if (!trimmed.startsWith('data:')) return;
    const payload = trimmed.slice(5).trim();
    if (!payload) return;
    let data;
    try {
      data = JSON.parse(payload);
    } catch {
      return;
    }
    if (data.chunk && onChunk) onChunk(data.chunk);
    if (data.done && onDone) onDone();
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 1);
        processLine(line);
      }
      if (done) break;
    }
    if (buffer.trim()) processLine(buffer);
  } catch (e) {
    if (e.name === 'AbortError') return;
    if (onError) onError(e);
  }
}

export function sessionHistoryToMessages(agentBackendName, history) {
  if (!Array.isArray(history)) return [];
  return history.map((entry, i) => ({
    id: `srv-${agentBackendName}-${i}-${entry.timestamp || ''}`,
    sender: entry.role === 'user' ? 'user' : 'agent',
    agentId: entry.role === 'assistant' ? agentBackendName : undefined,
    content: entry.content || '',
    timestamp: entry.timestamp || new Date().toISOString(),
    isPinned: false,
  }));
}
